import {ApiResource} from 'common/util/model-resource/api-resource';
import {Model} from 'common/util/model-resource/model-resource';
import {EventDefinition, EventDefinitionFull} from 'common/report/queries/app-api/data-definitions-types';

import {
  bulkUpdate,
  eventDefinitionsClient,
  eventDefinitionUniqueKey,
  getById,
  getByName,
  remove,
  update,
} from '../queries/query-api/event-definitions-client';

export class EventDefinitionsResource extends ApiResource<EventDefinition> {
  apiClient = eventDefinitionsClient;

  async get(id?: number, name?: string): Promise<EventDefinitionFull> {
    // the id[] is always parsed as numeric on the server. name[] is parsed as string
    const [response] = id && id > 0 ? await getById(id) : await getByName(name!);
    // make sure we cache it by the right key
    this.update({[eventDefinitionUniqueKey(response)]: response});

    return response;
  }

  /**
   *  TODO take in the whole Event Definition so we can encapsulate the identifying logic instead of relying on the caller
   * @param eventName Should be eventDef.apiName or use eventDefinitionUniqueKey(eventDefinition) because we look up by this key
   * @param eventDefUpdate
   * @returns
   */
  async patch(
    eventName: string,
    eventDefUpdate: Partial<EventDefinition>,
    transform?: (event: Partial<EventDefinition>) => EventDefinition,
  ): Promise<Model<EventDefinition>> {
    let updateResponse = await update(this.buildUpdatePayload(eventName, eventDefUpdate));

    /**
     * Optional transform to change API response before updating the store.
     */
    if (transform) {
      updateResponse = transform(updateResponse);
    }

    // individual Event Def patch returns the event def; bulk patch does not
    this.update({[eventName]: updateResponse});
    return this.getOne(eventName);
  }

  /**
   *
   * @param eventDefUpdates A Record, keyed by eventDef.apiName or the result of eventDefinitionUniqueKey(eventDefinition) because we look up by this key
   */
  async bulkPatch(eventDefUpdates: Record<string, Partial<EventDefinition>>): Promise<void> {
    const apiPayload = Object.entries(eventDefUpdates).map(([eventName, eventDefUpdate]) => {
      return this.buildUpdatePayload(eventName, eventDefUpdate);
    });
    await bulkUpdate(apiPayload);

    this.update(eventDefUpdates);
  }

  /**
   * Delete only deletes events with non-null id.
   */
  bulkDelete(eventDefinitions: EventDefinition[]): Promise<void> {
    eventDefinitions.forEach((definition) => {
      this.delete(definition.name);
    });

    return remove(
      eventDefinitions.map((definition) => ({
        collectEverythingEventId: definition.collectEverythingEventId,
        customEventId: definition.customEventId,
        id: definition.id,
      })),
    );
  }

  //** Builds a payload including the identifier fields that the API needs. Based on LexiconApi.getBaseEventParams() */
  buildUpdatePayload(eventName: string, eventDefUpdate: Partial<EventDefinition>) {
    const eventDef = this.getOne(eventName).value;

    const updatePayload = {
      ...eventDefUpdate,
    };

    // The order if specificity is important here - only send the most specific identifier
    if (eventDef?.collectEverythingEventId) {
      updatePayload.collectEverythingEventId = eventDef.collectEverythingEventId;
    } else if (eventDef?.customEventId) {
      updatePayload.customEventId = eventDef.customEventId;
    } else if (eventDef?.id) {
      updatePayload.id = eventDef.id;
    } else {
      updatePayload.name = eventName;
    }

    return updatePayload;
  }
}
